import React, { useRef } from 'react'

import type { MicPermissionState } from '../hooks/useMicPermission'
import { useLightMode } from '@/hooks/useLightMode'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'

import {
  AlertTriangleIcon,
  Box,
  Button,
  DarkProvider,
  Flex,
  MicIcon,
  Text,
  XCircleIcon,
  tokenVar,
  useOverlayStack,
} from '@hivewatch/dark'

/** The card width, glyph circle and glyph size of the ai-voice setup card. */
const CARD_WIDTH = '440px'
const GLYPH_CIRCLE_SIZE = '72px'
const GLYPH_SIZE = 32

/**
 * Above the emergency-call container (1401), the confirm dialog (1500) and the
 * Video Wall (2000), and below the first-time-use tour (2100). The microphone
 * is a prerequisite for placing a call, so this panel must never open behind
 * the surfaces that need it.
 */
const MIC_PERMISSION_Z_INDEX = 2050

interface MicPermissionModalIProps {
  isOpen: boolean
  onClose: () => void
  micState: MicPermissionState | null
  isBusy: boolean
  onRequestPermission: () => void
}

interface PanelIProps {
  body: string
  icon: React.ReactNode
  /**
   * Omitted where a request cannot succeed from the current state, so the
   * panel does not offer a button that the browser will silently refuse.
   */
  primaryText?: string
  title: string
}

/**
 * The guided microphone panel behind the e911 banner's "Enable" call to action
 * (ENG-32960). A direct port of the `StepCard` layout in ai-voice's
 * `ConsoleSetupCard` (hivewatch/ai-voice PR #107): same 440px card, 72px glyph
 * circle, centered copy and stacked buttons, so an operator who has seen the
 * voice console recognises this one.
 *
 * Deliberately only the microphone third of that screen. The notifications
 * step, the device picker, the audio unlock and the read-only console are all
 * properties of the voice console as a whole, and the web-app has no such
 * mode. Here the microphone is one feature's prerequisite, not the app's.
 *
 * The card is hand-rolled rather than taking dark's `Modal`, whose chamfered
 * outline, header row and corner close button the setup card does not have.
 * It still runs the same `useOverlayStack` machinery that `Modal` does, so it
 * keeps the focus trap, focus restore, Escape handling and overlay-stack
 * membership that the shared component would have provided.
 */
export const MicPermissionModal = ({
  isOpen,
  onClose,
  micState,
  isBusy,
  onRequestPermission,
}: MicPermissionModalIProps) => {
  const colorScheme = useLightMode()
  const { t } = useTranslation()
  const wrapperRef = useRef<HTMLDivElement>(null)

  // An in-flight request owns the panel: closing it mid-request would leave
  // the operator unsure whether the browser prompt was ever answered. The
  // tertiary button, the overlay and Escape are gated together.
  const handleDismiss = () => {
    if (isBusy) {
      return
    }
    onClose()
  }

  useOverlayStack({
    isOpen,
    onClose: handleDismiss,
    wrapperRef,
  })

  if (!isOpen || typeof document === 'undefined') {
    return null
  }

  // Dark is the palette the card was designed against; the light tokens are
  // prefixed so a light-mode operator still gets a readable card.
  const token = (name: string) =>
    colorScheme === 'light' ? `light.${name}` : name

  const panel = ((): PanelIProps => {
    if (micState === 'denied') {
      return {
        // No primary action: once an origin is blocked the browser refuses
        // getUserMedia outright and will not re-prompt, so the only route
        // back is site settings and a button here could only ever no-op.
        body: t('emergency_call:mic_blocked_body'),
        icon: (
          <XCircleIcon
            color={tokenVar(`colors.${token('icon.danger')}`)}
            size={GLYPH_SIZE}
          />
        ),
        title: t('emergency_call:mic_blocked_title'),
      }
    }

    if (micState === 'no-hardware') {
      return {
        body: t('emergency_call:mic_no_hardware_body'),
        icon: (
          <AlertTriangleIcon
            color={tokenVar(`colors.${token('status.warning')}`)}
            size={GLYPH_SIZE}
          />
        ),
        primaryText: t('emergency_call:mic_no_hardware_primary'),
        title: t('emergency_call:mic_no_hardware_title'),
      }
    }

    return {
      body: t('emergency_call:mic_prompt_body'),
      icon: <MicIcon colorScheme={colorScheme} size={GLYPH_SIZE} />,
      primaryText: t('emergency_call:mic_prompt_primary'),
      title: t('emergency_call:mic_prompt_title'),
    }
  })()

  return ReactDOM.createPortal(
    <DarkProvider>
      {/* data-no-focus-lock exempts this portal from any active react-focus-lock
          trap (e.g. a Chakra v1 Drawer with trapFocus), which would otherwise
          reclaim focus from the card on every click. */}
      <Box
        alignItems='center'
        bg={token('bg.overlay')}
        data-no-focus-lock
        display='flex'
        inset={0}
        justifyContent='center'
        onClick={handleDismiss}
        position='fixed'
        zIndex={MIC_PERMISSION_Z_INDEX}
      >
        <Flex
          align='center'
          aria-labelledby='mic-permission-title'
          aria-modal='true'
          bg={token('bg.card')}
          border='1px solid'
          borderColor={token('border.subtle')}
          data-testid='mic-permission-card'
          direction='column'
          maxW={CARD_WIDTH}
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
          outline='none'
          p={10}
          ref={wrapperRef}
          role='dialog'
          tabIndex={-1}
          textAlign='center'
          w='100%'
        >
          <Flex
            align='center'
            bg={token('bg.callout')}
            borderRadius='full'
            h={GLYPH_CIRCLE_SIZE}
            justify='center'
            mb={5}
            w={GLYPH_CIRCLE_SIZE}
          >
            {panel.icon}
          </Flex>
          <Text
            color={token('text.primary')}
            id='mic-permission-title'
            mb={2}
            textStyle='heading'
          >
            {panel.title}
          </Text>
          <Text color={token('text.secondary')} mb={6} textStyle='body'>
            {panel.body}
          </Text>
          <Flex direction='column' gap={3} w='100%'>
            {panel.primaryText && (
              <Button
                colorScheme={colorScheme}
                isLoading={isBusy}
                onClick={onRequestPermission}
                size='lg'
                variant='primary'
              >
                {panel.primaryText}
              </Button>
            )}
            <Button
              colorScheme={colorScheme}
              disabled={isBusy}
              onClick={handleDismiss}
              size='sm'
              variant='tertiary'
            >
              {t('emergency_call:mic_continue_without')}
            </Button>
          </Flex>
        </Flex>
      </Box>
    </DarkProvider>,
    document.body
  )
}
